import { useState } from "react";
import { Popover } from "@snpjs/core";

import { TableFilterProps } from "./TableFilter.types";
import styles from "./TableFilter.module.scss";

type DateRange = [string, string];

const addMonths = (date: Date, amount: number): Date =>
  new Date(date.getFullYear(), date.getMonth() + amount, 1);

const toDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const monthDays = (month: Date): Array<Date | null> => {
  const firstDay = new Date(month.getFullYear(), month.getMonth(), 1);
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const days: Array<Date | null> = Array(firstDay.getDay()).fill(null);

  for (let day = 1; day <= daysInMonth; day += 1) {
    days.push(new Date(month.getFullYear(), month.getMonth(), day));
  }

  while (days.length % 7 !== 0) days.push(null);
  return days;
};

interface CalendarMonthProps {
  month: Date;
  selectedRange: DateRange;
  onSelect: (date: string) => void;
  onMonthChange: (month: Date) => void;
}

const CalendarMonth: React.FC<CalendarMonthProps> = ({ month, selectedRange, onSelect, onMonthChange }) => {
  const [start, end] = selectedRange;
  const days = monthDays(month);
  const currentYear = new Date().getFullYear();
  const firstYear = Math.min(1900, month.getFullYear());
  const lastYear = Math.max(currentYear + 20, month.getFullYear());
  const years = Array.from({ length: lastYear - firstYear + 1 }, (_, index) => firstYear + index);
  const months = Array.from({ length: 12 }, (_, index) =>
    new Date(2000, index, 1).toLocaleString(undefined, { month: "long" })
  );

  return (
    <div className={styles.calendarMonth}>
      <div className={styles.monthTitle}>
        <select
          className={styles.calendarSelect}
          aria-label="Select month"
          value={month.getMonth()}
          onChange={(event) => onMonthChange(new Date(month.getFullYear(), Number(event.target.value), 1))}
        >
          {months.map((monthName, index) => (
            <option key={monthName} value={index}>{monthName}</option>
          ))}
        </select>
        <select
          className={styles.calendarSelect}
          aria-label="Select year"
          value={month.getFullYear()}
          onChange={(event) => onMonthChange(new Date(Number(event.target.value), month.getMonth(), 1))}
        >
          {years.map((year) => <option key={year} value={year}>{year}</option>)}
        </select>
      </div>
      <div className={styles.calendarGrid}>
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
          <span className={styles.weekday} key={day}>{day}</span>
        ))}
        {days.map((date, index) => {
          if (!date) return <span className={styles.emptyDay} key={`empty-${index}`} />;

          const dateKey = toDateKey(date);
          const isSelected = dateKey === start || dateKey === end;
          const isInRange = Boolean(start && end && dateKey > start && dateKey < end);

          return (
            <button
              className={[
                styles.calendarDay,
                isSelected ? styles.selectedDay : "",
                isInRange ? styles.inRangeDay : "",
              ].filter(Boolean).join(" ")}
              key={dateKey}
              type="button"
              aria-label={date.toLocaleDateString()}
              aria-pressed={isSelected}
              onClick={() => onSelect(dateKey)}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const DateFilterComponent: React.FC<TableFilterProps> = ({ value, onChange }) => {
  const [start, end] = (value as DateRange | undefined) ?? ["", ""];
  const [visibleMonth, setVisibleMonth] = useState(() => {
    const selectedStart = start ? new Date(`${start}T00:00:00`) : new Date();
    return new Date(selectedStart.getFullYear(), selectedStart.getMonth(), 1);
  });

  const selectDate = (date: string) => {
    if (!start || end) {
      onChange([date, ""]);
      return;
    }

    onChange(date < start ? [date, start] : [start, date]);
  };

  const dateRangePicker = (
    <div className={styles.datePopover}>
      <div className={styles.calendarNavigation}>
        <button
          className={styles.calendarNavigationButton}
          type="button"
          aria-label="Previous month"
          onClick={() => setVisibleMonth((month) => addMonths(month, -1))}
        >
          &lt;
        </button>
        <button
          className={styles.calendarNavigationButton}
          type="button"
          aria-label="Next month"
          onClick={() => setVisibleMonth((month) => addMonths(month, 1))}
        >
          &gt;
        </button>
      </div>
      <div className={styles.calendarMonths}>
        <CalendarMonth
          month={visibleMonth}
          selectedRange={[start, end]}
          onSelect={selectDate}
          onMonthChange={setVisibleMonth}
        />
        <CalendarMonth
          month={addMonths(visibleMonth, 1)}
          selectedRange={[start, end]}
          onSelect={selectDate}
          onMonthChange={(month) => setVisibleMonth(addMonths(month, -1))}
        />
      </div>
      <div className={styles.calendarFooter}>
        <span>{start || "Start"} to {end || "End"}</span>
        {(start || end) && (
          <button className={styles.clearButton} type="button" onClick={() => onChange(undefined)}>
            Clear dates
          </button>
        )}
      </div>
    </div>
  );
  const dateRangeTarget = (
    <div className={styles.dateRangeTarget}>
      <span className={styles.dateTargetValue}>{start || "YYYY-MM-DD"}</span>
      <span className={styles.dateRangeSeparator}>to</span>
      <span className={styles.dateTargetValue}>{end || "YYYY-MM-DD"}</span>
      {(start || end) && (
        <button
          className={styles.clearButton}
          type="button"
          aria-label="Clear date range"
          onClick={(event) => {
            event.stopPropagation();
            onChange(undefined);
          }}
        >
          ×
        </button>
      )}
    </div>
  );

  return (
    <Popover
      content={dateRangePicker}
      placement="bottom-start"
      usePortal={true}
    >
      {dateRangeTarget}
    </Popover>
  );
};